/**
 * API Configuration
 * Environment-based configuration for all API endpoints
 */

const ENV = process.env.NODE_ENV || 'development';

// API Base URLs
const API_URLS = {
  production: 'https://api.oneplace.hr',
  development: 'http://localhost:8080',
  staging: 'https://oneplace-hr-staging.asia-southeast1.run.app'
};

// AI Chatbot Base URLs
const AICHATBOT_URLS = {
  production: 'https://aichatbot-326159028339.asia-southeast1.run.app',
  development: 'http://localhost:8081',  // Different port from hrSystemServer (8080)
  staging: 'https://aichatbot-326159028339.asia-southeast1.run.app'
};

// Get base URL based on environment or use explicit env variable
export const API_BASE_URL = process.env.REACT_APP_API_URL || API_URLS[ENV] || API_URLS.development;

// AI Chatbot base URL
export const AICHATBOT_BASE_URL = process.env.REACT_APP_AICHATBOT_URL || AICHATBOT_URLS[ENV] || AICHATBOT_URLS.development;

// API Versions
export const API_VERSION = '/v1';

// Full API URL
export const API_URL = `${API_BASE_URL}${API_VERSION}`;

// Request timeout in milliseconds
export const REQUEST_TIMEOUT = 30000;

// Default headers
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

// Token storage keys
export const TOKEN_KEY = 'diil_auth_token';
export const REFRESH_TOKEN_KEY = 'diil_refresh_token';
export const USER_KEY = 'diil_user';

// API Endpoints paths
export const ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    // Candidate phone OTP (matches hrSystemServer CandidateOtpController)
    PHONE_SEND_OTP: '/auth/candidate/otp/send',
    PHONE_VERIFY: '/auth/candidate/otp/verify',
    PHONE_LOGIN: '/auth/candidate/phone',
    EMONGOLIA_SEND_OTP: '/auth/candidate/emongolia/send-otp',
    EMONGOLIA_VERIFY: '/auth/candidate/emongolia/verify',
    // Direct test access authentication (auto-login via test link)
    TEST_ACCESS: (accessToken) => `/test-candidate/auth/${accessToken}`,
    // One-click employer sign-in, from the SMS an admin sends a business they posted
    // ads for. Single-use and expiring server-side; POST so no link preview can spend it.
    EMPLOYER_LINK: (token) => `/auth/candidate/employer-link/${token}`
  },

  // Jobs
  //
  // WARNING: like COMPANIES below, the plural `/jobs/*` shape here has NO backend
  // route — hrSystemServer only serves `/job/:id` (singular, JOB_BY_ID) and
  // `/assessment/jobs/all` (ACTIVE). Apply/save go through candidateApply.js and
  // assessmentFavorite.js instead. Use ACTIVE / JOB_BY_ID; nothing should call the rest.
  JOBS: {
    // Public endpoints (no auth required)
    ACTIVE: '/assessment/jobs/all',
    HOME_META: '/assessment/jobs/home-meta',
    // Open-ad counts for every industry in one row each. Replaces asking ACTIVE once per
    // industry with size=1 just to read totalElements off it — see getJobCountsByIndustry.
    COUNT_BY_INDUSTRY: '/assessment/jobs/count-by-industry',
    JOB_BY_ID: (id) => `/job/${id}`
  },

  // Assessment Companies (public endpoints)
  ASSESSMENT_COMPANIES: {
    LIST: '/assessment/companies',
    // New endpoint: companies with isActive=true AND isPost=true assessment count + benefits
    LIST_WITH_BENEFITS: '/assessment/companies/list',
    JOBS: (id) => `/assessment/companies/${id}`,
    // Public — anyone with a company's share link, no login. See CompanyDetail.jsx.
    JOB_APPLICANTS_PUBLIC: (companyId, jobId) => `/assessment/companies/${companyId}/jobs/${jobId}/applicants`,
    // Authenticated (any signed-in candidate, not ownership-scoped) — deliberately under
    // /diil/employer so it is never covered by the public matcher above.
    APPLICANT_CV_AUTH: (companyId, jobId, candidateId) =>
      `/diil/employer/companies/${companyId}/jobs/${jobId}/applicants/${candidateId}/cv`,
  },

  // Companies — hrSystemServer serves companies under /v1/company* (singular) and
  // /v1/assessment/companies*, not /v1/companies*. Use ASSESSMENT_COMPANIES above for
  // listings; this is only the name-autocomplete endpoint.
  COMPANIES: {
    NAMES: '/company/names'
  },

  // CV/Resume
  CV: {
    // JWT-based endpoints (recommended for authenticated users)
    ME: '/cv/me',  // GET = get my CV, POST = find or create my CV
    UPDATE: (cvId) => `/cv/${cvId}`,
    // Education
    ADD_EDUCATION: (cvId) => `/cv/${cvId}/education`,
    UPDATE_EDUCATION: (cvId, eduId) => `/cv/${cvId}/education/${eduId}`,
    DELETE_EDUCATION: (cvId, eduId) => `/cv/${cvId}/education/${eduId}`,
    // Experience
    ADD_EXPERIENCE: (cvId) => `/cv/${cvId}/experience`,
    UPDATE_EXPERIENCE: (cvId, expId) => `/cv/${cvId}/experience/${expId}`,
    DELETE_EXPERIENCE: (cvId, expId) => `/cv/${cvId}/experience/${expId}`,
    // Skills
    ADD_SKILL: (cvId) => `/cv/${cvId}/skill`,
    UPDATE_SKILL: (cvId, skillId) => `/cv/${cvId}/skill/${skillId}`,
    DELETE_SKILL: (cvId, skillId) => `/cv/${cvId}/skill/${skillId}`,
    // Languages
    ADD_LANGUAGE: (cvId) => `/cv/${cvId}/language`,
    UPDATE_LANGUAGE: (cvId, langId) => `/cv/${cvId}/language/${langId}`,
    DELETE_LANGUAGE: (cvId, langId) => `/cv/${cvId}/language/${langId}`,
    // Certificates
    ADD_CERTIFICATE: (cvId) => `/cv/${cvId}/certificate`,
    UPDATE_CERTIFICATE: (cvId, certId) => `/cv/${cvId}/certificate/${certId}`,
    DELETE_CERTIFICATE: (cvId, certId) => `/cv/${cvId}/certificate/${certId}`,
    // Projects
    ADD_PROJECT: (cvId) => `/cv/${cvId}/project`,
    UPDATE_PROJECT: (cvId, projId) => `/cv/${cvId}/project/${projId}`,
    DELETE_PROJECT: (cvId, projId) => `/cv/${cvId}/project/${projId}`,
    // Publications
    ADD_PUBLICATION: (cvId) => `/cv/${cvId}/publication`,
    UPDATE_PUBLICATION: (cvId, pubId) => `/cv/${cvId}/publication/${pubId}`,
    DELETE_PUBLICATION: (cvId, pubId) => `/cv/${cvId}/publication/${pubId}`,
    // Awards
    ADD_AWARD: (cvId) => `/cv/${cvId}/award`,
    UPDATE_AWARD: (cvId, awardId) => `/cv/${cvId}/award/${awardId}`,
    DELETE_AWARD: (cvId, awardId) => `/cv/${cvId}/award/${awardId}`,
    // Hobbies
    ADD_HOBBY: (cvId) => `/cv/${cvId}/hobby`,
    UPDATE_HOBBY: (cvId, hobbyId) => `/cv/${cvId}/hobby/${hobbyId}`,
    DELETE_HOBBY: (cvId, hobbyId) => `/cv/${cvId}/hobby/${hobbyId}`,
    // Volunteers
    ADD_VOLUNTEER: (cvId) => `/cv/${cvId}/volunteer`,
    UPDATE_VOLUNTEER: (cvId, volId) => `/cv/${cvId}/volunteer/${volId}`,
    DELETE_VOLUNTEER: (cvId, volId) => `/cv/${cvId}/volunteer/${volId}`,
    // Memberships
    ADD_MEMBERSHIP: (cvId) => `/cv/${cvId}/membership`,
    UPDATE_MEMBERSHIP: (cvId, memId) => `/cv/${cvId}/membership/${memId}`,
    DELETE_MEMBERSHIP: (cvId, memId) => `/cv/${cvId}/membership/${memId}`,
  },

  // CV Snapshot
  CV_SNAPSHOT: {
    CREATE: '/cv-snapshot',
    GET: (snapshotId) => `/cv-snapshot/${snapshotId}`,
    GET_BY_APPLICATION: (apId) => `/cv-snapshot/by-application/${apId}`,
  },

  // Chat
  CHAT: {
    LIST: '/chat',
    MESSAGES: (chatId) => `/chat/${chatId}/messages`,
    SEND: (chatId) => `/chat/${chatId}/send`
  },

  // Games
  GAMES: {
    SAVE: '/game/save',
    LEADERBOARD: '/game/leaderboard',
    USER_SCORES: (userId) => `/game/user/${userId}/scores`
  },

  // Feedback
  FEEDBACK: {
    SUBMIT: '/feedback',
    LIST: '/feedback'
  },

  // Self-serve employer posting (Diil "Зар нийтлэх" wizard).
  // Authenticated — these require the candidate token an employer signs in with.
  EMPLOYER: {
    ME: '/diil/employer/me',
    RESOLVE_COMPANY: '/diil/employer/company/resolve',
    // What registering would do (CREATE | JOIN | ALREADY_MEMBER | TAKEN). Writes nothing.
    REGISTER_PREVIEW: '/diil/employer/company/register-preview',
    // The employer's own company page. No id in either path — the server resolves the
    // company from the token, so there is nothing to point at someone else's.
    COMPANY_PROFILE: '/diil/employer/company',
    COMPANY_PHOTO: '/diil/employer/company/photo',
    // Same replace-the-logo action, but for a company an admin posted for and doesn't
    // belong to — companyId in the path since there is no "my company" to resolve from
    // an admin's token. Same admin-only authorization shape as NOTIFY_COMPANY below.
    COMPANY_PHOTO_BY_ID: (companyId) => `/diil/employer/company/${companyId}/photo`,
    // Which company a contact number already belongs to. Admins only, server-side — it is
    // what stops a re-typed company name from creating a second page for one business.
    COMPANY_BY_PHONE: (phone) => `/diil/employer/company/by-phone?phone=${encodeURIComponent(phone)}`,
    // Texts the company's registered contact that applicants have come in — admin only,
    // for a business an admin posted for that never logs into Diil itself.
    NOTIFY_COMPANY: (companyId) => `/diil/employer/company/${companyId}/notify-applicants`,
    JOBS: '/diil/employer/jobs',
    JOB: (id) => `/diil/employer/jobs/${id}`,
    JOB_APPLICANTS: (id) => `/diil/employer/jobs/${id}/applicants`,
    APPLICANT_CV: (id, candidateId) => `/diil/employer/jobs/${id}/applicants/${candidateId}/cv`,
    // The CV-bank cards for everyone on one ad — the same rows /diil/admin/cvs returns, but
    // authorized by owning the posting rather than by being an admin, so an ordinary
    // employer's applicants list can draw the same card the admin's does.
    APPLICANT_CV_ROWS: (id) => `/diil/employer/jobs/${id}/applicant-cv-rows`,
    // One applicant's AI interview result, keyed by their assessment_player id — which is
    // exactly the `id` JOB_APPLICANTS returns for each row.
    //
    // NOT a /diil/employer route: this is hrSystemServer's own shared endpoint, and it
    // checks that you are signed in but not that the row is yours. So the only ids that
    // may be passed here are ones the employer's own applicants list handed us.
    APPLICANT_INTERVIEW: (assessmentPlayerId) => `/assPlayer/${assessmentPlayerId}`,
    // Where the employer has got to with one applicant. Stored server-side, because the
    // candidate's own application list reads the same field back.
    APPLICANT_STATUS: (jobId, applicantId) =>
      `/diil/employer/jobs/${jobId}/applicants/${applicantId}/status`
  },

  // Stored notifications — the ones that are a real row rather than something the client
  // rebuilds from data recorded elsewhere. Only "Ажил санал болгох" writes here today.
  //
  // The store is general, so reading is; creating one is not, hence the verb-shaped send
  // path. Who may send which offer is decided server-side by ad ownership, and MINE reads
  // the candidate off the token — there is deliberately no ?candidateId= on it.
  NOTIFICATIONS: {
    JOB_OFFER: '/diil/notifications/job-offer',
    MINE: '/diil/notifications/me'
  },

  // The admin CV bank (/admin/cv). Every candidate's CV, filterable, plus one in full.
  //
  // Authenticated AND admin-only, checked server-side against the caller's phone in the
  // users table — the same check that fills the `admin` flag on EMPLOYER.ME above. The
  // client-side guard on the page only decides what to draw; this is what actually refuses.
  //
  // DETAIL is keyed by candidate id, not CV id, so it lines up with CANDIDATES.* and the
  // employer applicant routes. It returns the FULL CV, phone and email included — unlike
  // the public shapes, which strip exactly those.
  ADMIN_CV: {
    LIST: '/diil/admin/cvs',
    DETAIL: (candidateId) => `/diil/admin/cvs/${candidateId}`,
    // The same list rows, for people a screen already knows by id — so "Миний зарууд" can
    // draw its applicants with the CV bank's own card instead of a thinner one.
    BY_CANDIDATES: '/diil/admin/cvs/by-candidates',
    // AI-ranked free-text search: the admin's typed description is judged directly against
    // real candidate profiles, returning a ranked shortlist with reasons — not a translation
    // into LIST's filter fields. See adminCv.js:aiSearchCvs.
    AI_SEARCH: '/diil/admin/cvs/ai-search',
    // Toggles the CV bank's shared shortlist. Shared, not per-admin: one admin saving a CV
    // saves it for the team, and the card says whose pick it was.
    FAVORITE: (candidateId) => `/diil/admin/cvs/${candidateId}/favorite`
  },

  // Automated AI phone calls placed from the CV bank (/admin/cv).
  //
  // Same admin gate as ADMIN_CV — checked server-side against an active ADMIN row in `users`.
  // The bridge token that actually rings a telephone never comes near the browser: START is
  // our own route, and the server talks to the bridge.
  //
  // BATCH is the bridge's live counters for a running batch (queued / dialing / done /
  // failed). The batch id is NOT stored anywhere — it comes back from START and lives only
  // in the tab that asked, so a reload loses the progress bar but not the results: those
  // arrive by webhook and land on each candidate's own row.
  //
  // AUDIO is the recording, proxied from CallHub. Its own route rather than
  // /v1/rtc/call-log/{id}/audio, which is keyed by a call_recordings row — AI calls do not
  // create one, so the CallHub id stored on the candidate is the only handle there is.
  AI_CALL: {
    START: '/diil/admin/ai-calls',
    BATCH: (batchId) => `/diil/admin/ai-calls/batch/${batchId}`,
    // STATS is the whole picture rather than one batch: how many people have been rung,
    // how many were actually reached, and how each of them ended. Counts PEOPLE — only the
    // last call to somebody is stored — so ringing the same person twice moves them between
    // buckets instead of adding one.
    STATS: '/diil/admin/ai-calls/stats',
    // The saved prompts: for each name, the newest system prompt ever sent under it. There
    // is no prompt table — calling with an edited prompt is what saves the edit.
    PROMPTS: '/diil/admin/ai-calls/prompts',
    AUDIO: (candidateId) => `/diil/admin/ai-calls/${candidateId}/audio`,
    // Every call ever placed to one person, newest first. The candidate's own row carries
    // only the last one — it is what the bank filters on — so this is where an earlier
    // attempt, and the recording of it, can still be read.
    HISTORY: (candidateId) => `/diil/admin/ai-calls/${candidateId}/history`,
    // One past call's recording. `callId` is our own history row id, never CallHub's: the
    // server will not take a CallHub id from a browser (it can read any call in that
    // account), so the pairing is checked server-side.
    CALL_AUDIO: (candidateId, callId) =>
      `/diil/admin/ai-calls/${candidateId}/audio/${callId}`
  },

  // A Diil admin's own calls to a candidate, over CallHub — dialled from the browser
  // (SIP.js), logged, recorded and transcribed. Rows live in call_recordings with
  // source="diil"; the AI bot's calls stay under AI_CALL. All admin-only server-side, and
  // RTC_CONFIG carries the SIP password, which is why it is not /rtc/config (any token).
  MANUAL_CALL: {
    RTC_CONFIG: '/diil/admin/calls/rtc-config',
    CREATE: '/diil/admin/calls',
    UPDATE: (id) => `/diil/admin/calls/${id}`,
    AUDIO: (id) => `/diil/admin/calls/${id}/audio`,
    HISTORY: (candidateId) => `/diil/admin/calls/candidate/${candidateId}`,
    EVENTS: '/diil/admin/calls/events'
  },

  // The admin job board (/admin/jobs). Every ad on Diil with its funnel counted beside it,
  // and the applicants behind any one of them.
  //
  // Same admin gate as ADMIN_CV above — the caller's phone against an active ADMIN row in
  // `users`, checked server-side. Deliberately a separate path from ADMIN_CV rather than a
  // branch of it: the sidebar marks a row active with `startsWith`, so a route nested under
  // /admin/cv would light up "CV сан" as well.
  //
  // LIST rows carry viewCount / applicantCount / interviewStartedCount /
  // interviewCompletedCount — the funnel, read off assessment_players and job_views in one
  // aggregate query, not one request per ad. APPLICANTS carries each person's interview
  // score and summary on the row, so the list needs no per-applicant follow-up call (which
  // is what the employer's own "Миний зар" has to do via /assPlayer/{id}).
  ADMIN_JOBS: {
    LIST: '/diil/admin/jobs',
    APPLICANTS: (jobId) => `/diil/admin/jobs/${jobId}/applicants`
  },

  // Diil "Ажлын Групп" feed (/job-group). GET is public; writes need a candidate token.
  GROUP_POSTS: {
    LIST: '/diil/group-posts',
    CREATE: '/diil/group-posts',
    DELETE: (id) => `/diil/group-posts/${id}`,
    LIKE: (id) => `/diil/group-posts/${id}/like`,
    COMMENTS: (id) => `/diil/group-posts/${id}/comments`,
    // Replies on the caller's OWN posts, for the notifications page. Not "/mine/comments":
    // the public rule for one post's thread is `/diil/group-posts/*/comments`, and `*`
    // would match "mine" — this path needs a token.
    MY_COMMENTS: '/diil/group-posts/my-comments',
    // Likes on the caller's OWN posts, for the notifications page. Authenticated for the
    // same reason as MY_COMMENTS.
    MY_LIKES: '/diil/group-posts/my-likes',
    // One candidate's own posts, for the Ажлын групп section of their public profile.
    // Anonymous posts are withheld server-side.
    BY_CANDIDATE: (id) => `/diil/group-posts/by-candidate/${id}`,
    COMMENT: (id, commentId) => `/diil/group-posts/${id}/comments/${commentId}`,
    // The jobs the backend's matching agent chose for a post. The feed already carries
    // them inline; this is for polling a post that is still being answered.
    SUGGESTIONS: (id) => `/diil/group-posts/${id}/suggestions`
  },

  // The five curated Diil sectors (Салбар cards, home banners, wizard step 1).
  DIIL_SECTORS: {
    LIST: '/diil/sectors'
  },

  // Mongolia's administrative divisions, for the location pickers.
  LOCATIONS: {
    LIST: '/location/all'
  },

  // CV builder autocomplete corpora.
  CV_REFERENCE: {
    COMPANIES: '/cv-reference/companies',
    POSITIONS: '/cv-reference/positions',
    UNIVERSITIES: '/cv-reference/universities',
    // Batch logo lookup for names already saved on a CV — keys are normalized
    // client-side and joined with "|". See src/data/logoDirectory.js.
    LOGOS: '/cv-reference/logos',
    // Logos of companies registered on this platform — their own uploaded marks, which
    // the reference directory does not have. Matched client-side.
    REGISTERED_LOGOS: '/cv-reference/registered-logos'
  },

  // Reference Data (Industries)
  INDUSTRIES: {
    LIST: '/industries',
    DETAIL: (id) => `/industries/${id}`,
    BY_SLUG: (slug) => `/industries/slug/${slug}`
  },

  // Industry Sub-categories.
  // LIST returns the curated home-page categories only. The same table also holds the
  // Чиглэл -> Албан тушаал tree behind the post-job wizard, reachable via BY_INDUSTRY
  // (directions in a sector) and BY_PARENT (positions in a direction).
  INDUSTRY_SUBS: {
    LIST: '/industry-subs',
    DETAIL: (id) => `/industry-subs/${id}`,
    BY_SLUG: (slug) => `/industry-subs/slug/${slug}`,
    BY_INDUSTRY: '/industry-subs',
    BY_PARENT: '/industry-subs',
    SEARCH: '/industry-subs/search'
  },

  // Job Levels (experience levels)
  JOB_LEVELS: {
    LIST: '/job-levels',
    DETAIL: (id) => `/job-levels/${id}`
  },

  // Branches
  BRANCHES: {
    BY_COMPANY: (companyId) => `/branch/public/company/${companyId}`
  },

  // Candidates (profile data)
  CANDIDATES: {
    // JWT-based endpoints (recommended for authenticated users)
    ME: '/chat/profile/me',  // GET = get my profile, PUT = update my profile
    // ID-based endpoints
    UPDATE_PROFILE: (id) => `/candidates/${id}/profile`,
    SEARCH: '/candidates/search',
    UPLOAD_PHOTO: (id) => `/candidates/${id}/photo`,
    MANUAL_COMPLETE: '/candidates/manual-complete',
    EMONGOLIA_COMPLETE: '/candidates/emongolia-complete',
    // Any signed-in candidate may open this for any other candidate id — no job/
    // application relationship required. The backend removed the plain single-candidate
    // GET for IDOR; this narrow, redacted-profile route is what replaced it.
    PUBLIC_PROFILE: (id) => `/diil/candidates/${id}/public-profile`
  },

  // Assessment Player (candidate stage info)
  ASSESSMENT_PLAYER: {
    // Get stage info for all of the current user's applications
    MY_STAGES: '/assessmentPlayer/my-stages'
  },

  // Test Candidate (candidate test assignments)
  TEST_CANDIDATE: {
    // === JWT-BASED ENDPOINTS (recommended - no candidateId needed) ===
    // Get all tests for current user (via JWT)
    MY_ALL: '/test-candidate/my/all',
    // Get pending tests for current user (via JWT)
    MY_PENDING: '/test-candidate/my/pending',
    // Get completed tests for current user (via JWT)
    MY_COMPLETED: '/test-candidate/my/completed',
    // === ASSESSMENT PLAYER ID BASED ENDPOINTS (legacy) ===
    // Get all tests for a candidate (by assessmentPlayerId)
    BY_AP: (apId) => `/test-candidate/assessment-player/${apId}`,
    // Get pending tests for a candidate (by assessmentPlayerId)
    PENDING: (apId) => `/test-candidate/assessment-player/${apId}/pending`,
    // Get completed tests for a candidate (by assessmentPlayerId)
    COMPLETED: (apId) => `/test-candidate/assessment-player/${apId}/completed`,
    // Get tests at a specific stage (by assessmentPlayerId)
    BY_STAGE: (apId, stageId) => `/test-candidate/assessment-player/${apId}/stage/${stageId}`,
    // Get single test assignment
    GET: (id) => `/test-candidate/${id}`,
    // Get test assignment by secure access token (UUID)
    BY_ACCESS_TOKEN: (accessToken) => `/test-candidate/access/${accessToken}`,
    // Start a test
    START: (id) => `/test-candidate/${id}/start`,
    // Complete a test
    COMPLETE: (id) => `/test-candidate/${id}/complete`,
    // Pause a test
    PAUSE: (id) => `/test-candidate/${id}/pause`,
    // === CANDIDATE ID BASED ENDPOINTS (for when candidateId is available) ===
    // Get all tests by candidateId
    BY_CANDIDATE: (candidateId) => `/test-candidate/candidate/${candidateId}`,
    // Get pending tests by candidateId
    PENDING_BY_CANDIDATE: (candidateId) => `/test-candidate/candidate/${candidateId}/pending`,
    // Get completed tests by candidateId
    COMPLETED_BY_CANDIDATE: (candidateId) => `/test-candidate/candidate/${candidateId}/completed`,
    // Get tests at stage by candidateId
    BY_CANDIDATE_STAGE: (candidateId, stageId) => `/test-candidate/candidate/${candidateId}/stage/${stageId}`
  },

  // Test Questions (get questions for a test)
  TEST_QUESTION: {
    // Get all questions for a test by testId
    BY_TEST: (testId) => `/test-question/test/${testId}`,
    // Get total score for a test
    TOTAL_SCORE: (testId) => `/test-question/test/${testId}/total-score`
  },

  // Test Candidate Answer (for Diil - candidate test answers)
  TEST_ANSWER: {
    // Submit answers in batch (candidate endpoint)
    SUBMIT_BATCH: '/test-employee-answer/candidate/submit-batch',
    // Save partial answer (auto-save, candidate endpoint)
    SAVE_PARTIAL: '/test-employee-answer/candidate/save-partial',
    // Get saved answers for a test-candidate
    BY_TEST_CANDIDATE: (id) => `/test-employee-answer/test-candidate/${id}`,
    // Get final score for a test-candidate
    SCORE: (id) => `/test-employee-answer/test-candidate/${id}/score`
  },

  // Calendar (booking system - public endpoints)
  CALENDAR: {
    BY_URL: (url) => `/calendar/url/${encodeURIComponent(url)}`,
    AUTH: (token) => `/calendar/meeting/auth/${token}`,
    MEETING_BOOKING: '/calendar/meeting/booking',
    MEETING_BY_TOKEN: (token) => `/calendar/meeting/by-token/${token}`,
    MEETING_UPDATE: (id) => `/calendar/meeting/${id}`,
    CANDIDATE_BY_TOKEN: (token) => `/assessment/player/by-token/${token}`
  }
};

// AI Chatbot Endpoints (separate base URL)
export const AICHATBOT_ENDPOINTS = {
  // Conversation list for current user (legacy, loads full history)
  LIST: '/chat/list',
  // Optimized conversation list (~90% faster, uses denormalized counters)
  LIST_FAST: '/chat/list-fast',
  // Conversation count for current user (lightweight, for profile stats)
  COUNT: '/chat/count',
  // Initialize/get conversation for specific job
  INIT: (companyId, jobId) => `/chat/init/${companyId}/${jobId}`,
  // Send message in conversation
  CONVERSATION: '/chat/conversation',
  // Upload voice message (returns audioUrl to send to CONVERSATION)
  VOICE_UPLOAD: '/chat/conversation/voice',
  // Upload image attachment (returns { imageUrl, imagePath, mimeType })
  IMAGE_UPLOAD: '/chat/conversation/image',
  // Attach uploaded image to conversation history (no AI call)
  IMAGE_ATTACH: '/chat/conversation/image/attach',
  // Get conversation history by phone/telegram
  HISTORY: '/chat/player/history'
};

export default {
  API_BASE_URL,
  AICHATBOT_BASE_URL,
  API_VERSION,
  API_URL,
  REQUEST_TIMEOUT,
  DEFAULT_HEADERS,
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  ENDPOINTS,
  AICHATBOT_ENDPOINTS
};
